"use client"

import React from "react"
import { useTranslation } from "react-i18next"
import { DateTime } from "luxon";
import { ChevronDown, ChevronLeft, ChevronRight, Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { useCalendar } from "@/contexts/CalendarContext"
import { useSelectedStaff } from "@/contexts/SelectedStaffContext"
import type { Staff } from "@/lib/types/staff"
import { Avatar } from "@/components/ui/avatar"
import { useAllStaff } from "@/hooks/calendar/useAllStaff"
import { dateSource } from "@/lib/dateSource"
import { useCalendarData } from "@/contexts/CalendarDataProvider"
import { useDayReport } from "@/hooks/company/useDayReport"
import { salonNow } from "@/lib/time"
import { useAccessPolicy } from "@/hooks/access/useAccess"
import { earliestAllowedDate } from "@/lib/auth/dateLimit"
import { useSidebar } from "@/contexts/SidebarContext"
import { useIsMobile } from "@/hooks/use-mobile"
import { useCurrencySymbol } from "@/hooks/shared/useCurrencySymbol"
import { SalonSwitcher } from "@/components/SalonSwitcher"
import { ChatsButton } from "@/components/inbox/InboxEntry"

export function TopBar() {
  const { t, i18n } = useTranslation()
  const { currentDate, setDate } = useCalendar()
  const { } = useCalendarData();
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const currencySymbol = useCurrencySymbol();

  const formatDate = (date: DateTime) => {
    if (!date || !date.isValid) return "—";
    const day = date.day;
    const locale = i18n.language || "ru";
    const month = date.setLocale(locale).toFormat("LLLL");
    const dayOfWeek = date.setLocale(locale).toFormat("cccc");
    return `${day} ${month}, ${dayOfWeek}`;
  }

  const formatMobileDate = React.useCallback((d: DateTime) => {
    if (!d || !d.isValid) return "—";
    const locale = i18n.language || "ru";
    const localized = d.setLocale(locale);
    const weekday = localized.toFormat("cccc");
    const oneLetter = (weekday[0] || "").toLowerCase() + ".";
    const month = localized.toFormat("LLLL");
    return `${localized.day} ${month}, ${oneLetter}`;
  }, [i18n.language]);

  const handleTodayClick = () => setDate(dateSource.now())
  const handlePrevClick = async () => { if (!canGoPrev) return; setDate(currentDate.minus({ days: 1 })); }
  const handleNextClick = async () => { setDate(currentDate.plus({ days: 1 })); }

  // --- selection state
  const { selectedStaffId, setSelectedStaffId } = useSelectedStaff()
  const [staffDropdownOpen, setStaffDropdownOpen] = React.useState(false)
  const [staffList, setStaffList] = React.useState<Staff[]>([])
  const handleStaffDropdownOpenChange = (open: boolean) => setStaffDropdownOpen(open)

  // --- RBAC policy (we need forcedId BEFORE fetching staff)
  const policy = useAccessPolicy();
  const earliest = earliestAllowedDate(policy);
  const canGoPrev = currentDate.startOf("day") > earliest;
  const forcedId = policy.timetable.forcedStaffId ?? null;

  // --- ✅ fetch staff when needed (dropdown open OR a staff is selected/forced)
  const shouldLoadStaff = staffDropdownOpen || selectedStaffId != null || forcedId != null;
  const { data: staffListData, isLoading: isLoadingStaff } = useAllStaff(shouldLoadStaff);

  React.useEffect(() => {
    if (staffListData) setStaffList(staffListData);
  }, [staffListData]);

  // Resolve staff objects (note: may be undefined while data is loading)
  const selectedStaff = selectedStaffId != null ? staffList.find(s => Number(s.id) === Number(selectedStaffId)) : null
  const forcedStaff = forcedId != null ? staffList.find(s => Number(s.id) === Number(forcedId)) : null

  // 🔑 single source of truth for "single-staff mode"
  const effectiveStaffId = forcedId ?? selectedStaffId
  const effectiveStaff = forcedStaff ?? selectedStaff

  // Mirror RBAC forced id into selection (and only clear after leaving forced)
  const prevForcedIdRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (forcedId != null) {
      if (selectedStaffId !== forcedId) setSelectedStaffId(forcedId);
    } else if (prevForcedIdRef.current != null) {
      setSelectedStaffId(null);
    }
    prevForcedIdRef.current = forcedId ?? null;
  }, [forcedId, selectedStaffId, setSelectedStaffId]);

  // Day report summary logic:
  // - Past selected day → show that day's report
  // - Today or any future selected day → show today's report
  const today = salonNow().startOf("day");
  const selectedDay = currentDate.startOf("day");
  const selectedIso = currentDate.toISODate();              // string | null
  const todayIso    = today.toISODate();                    // string | null
  const summaryIso  = selectedDay < today ? selectedIso : todayIso;
  const {
    data: dayReport,
    isLoading: isLoadingDayReport,
    isError: isErrorDayReport,
  } = useDayReport(summaryIso);
  const dayReportAmount = dayReport
    ? currencySymbol
      ? `${dayReport.transaction_sum} ${currencySymbol}`
      : dayReport.transaction_sum
    : "—";

  return (
    <header className="topbar">
      {/* Mobile layout with hamburger + wrapped pills */}
      {isMobile ? (
        <>
          {/* Row 1: Hamburger + Today + Arrows + Date (wraps on very small screens) */}
          <div className="topbar__mobile-row">
            <button
              className="topbar__hamburger"
              onClick={() => toggleSidebar()}
              aria-label={t("topbar.open_menu", "Open menu")}
            >
              <Menu size={20} />
            </button>
            <div className="topbar__mobile-pills">
              <Button variant="ghost" className="pill" onClick={handleTodayClick}>
                {t("topbar.today", "Today")}
              </Button>
              <span className="pill">
                <Button variant="ghost" size="icon" className="p-1 h-6 w-8" onClick={handlePrevClick}>
                  <ChevronLeft size={4} />
                </Button>
                <Button variant="ghost" size="icon" className="p-1 h-6 w-8" onClick={handleNextClick}>
                  <ChevronRight size={4} />
                </Button>
              </span>
              <button
                className="pill cursor-pointer topbar__date-pill"
                title={currentDate.setLocale(i18n.language || "ru").toFormat("d LLLL, cccc")}
                onClick={() => toggleSidebar()}
                aria-label={t("topbar.open_calendar", "Open calendar")}
              >
                <span className="font-medium truncate min-w-0">
                  {formatMobileDate(currentDate)}
                </span>
              </button>
            </div>
          </div>
          {/* Row 2: Amount + Staff selector */}
          <div className="topbar__mobile-row topbar__mobile-row--right">
            <ChatsButton />
            {forcedId == null && (
              <span className="pill">
                {isLoadingDayReport
                  ? <span className="animate-pulse">...</span>
                  : isErrorDayReport || !dayReport
                    ? "—"
                    : dayReportAmount
                }
              </span>
            )}
            <DropdownMenu onOpenChange={handleStaffDropdownOpenChange}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="pill" disabled={isLoadingStaff || !!forcedId}>
                  {forcedStaff
                    ? forcedStaff.name
                    : selectedStaff
                      ? selectedStaff.name
                      : t("topbar.all_positions", "All positions")}
                  <ChevronDown size={16} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              {!forcedId && (
                <DropdownMenuContent>
                  <DropdownMenuItem onSelect={() => setSelectedStaffId(null)} disabled={selectedStaffId === null}>
                    {t("topbar.all_positions", "All positions")}
                  </DropdownMenuItem>
                  {isLoadingStaff ? (
                    <div className="py-2 px-4 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-pulse flex space-x-1">
                          <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                          <div className="w-1 h-1 bg-gray-500 rounded-full animation-delay-200"></div>
                          <div className="w-1 h-1 bg-gray-500 rounded-full animation-delay-400"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    staffList.map((staff) => (
                      <DropdownMenuItem
                        key={staff.id}
                        onSelect={() => setSelectedStaffId(Number(staff.id))}
                        disabled={Number(selectedStaffId) === Number(staff.id)}
                      >
                        <span>{staff.name}</span>
                        {staff.specialization && (
                          <span className="ml-1.5 text-[11px] text-muted-foreground">
                            ({staff.specialization})
                          </span>
                        )}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          </div>
        </>
      ) : (
        /* Desktop layout - unchanged */
        <>
          <div></div>
          <div className="topbar__left topbar__group px-4 min-w-0">
            <Button variant="ghost" className="pill" onClick={handleTodayClick}>
              {t("topbar.today", "Today")}
            </Button>
            {effectiveStaffId != null ? (
              <div className="flex items-center ml-4">
                <Avatar className="w-10 h-10 mr-2">
                  {effectiveStaff?.avatar ? (
                    <img
                      src={effectiveStaff.avatar}
                      alt={effectiveStaff?.name ?? t("common.staff_member", "Staff member")}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gray-200" />
                  )}
                </Avatar>
                <span className="text-xl font-medium">
                  {effectiveStaff?.name ?? " "}
                </span>
              </div>
            ) : (
              <>
                <span className="pill" title={currentDate.setLocale(i18n.language || "ru").toFormat("d LLLL, cccc")}>
                  <Button variant="ghost" size="icon" className="p-1 h-6 w-8" onClick={handlePrevClick}>
                    <ChevronLeft size={4} />
                  </Button>
                  <Button variant="ghost" size="icon" className="p-1 h-6 w-8" onClick={handleNextClick}>
                    <ChevronRight size={4} />
                  </Button>
                </span>
                <span className="pill" title={currentDate.setLocale(i18n.language || "ru").toFormat("d LLLL, cccc")}>
                  <span className="font-medium truncate min-w-0">
                    {formatDate(currentDate)}
                  </span>
                </span>
              </>
            )}
          </div>
        </>
      )}

      {/* Desktop right section only */}
      {!isMobile && (
        <div className="ml-auto flex items-center topbar__right topbar__group">
          {/* Multi-salon: renders only when the login has >1 salon */}
          <span className="mr-2"><SalonSwitcher /></span>
          {/* Boona Direct: only when the module is on and the user may use it */}
          <ChatsButton className="mr-2" />
          {/* hide day report when a staff is forced */}
          {forcedId == null && (
            <span className="pill mr-2">
              {isLoadingDayReport
                ? <span className="animate-pulse">...</span>
                : isErrorDayReport || !dayReport
                  ? "—"
                  : dayReportAmount
              }
            </span>
          )}

          <DropdownMenu onOpenChange={handleStaffDropdownOpenChange}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="pill mr-2" disabled={isLoadingStaff || !!forcedId}>
                {forcedStaff
                  ? forcedStaff.name
                  : selectedStaff
                    ? selectedStaff.name
                    : t("topbar.all_positions", "All positions")}
                <ChevronDown size={16} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>

            {!forcedId && (
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setSelectedStaffId(null)} disabled={selectedStaffId === null}>
                  {t("topbar.all_positions", "All positions")}
                </DropdownMenuItem>

                {isLoadingStaff ? (
                  <div className="py-2 px-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-pulse flex space-x-1">
                        <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                        <div className="w-1 h-1 bg-gray-500 rounded-full animation-delay-200"></div>
                        <div className="w-1 h-1 bg-gray-500 rounded-full animation-delay-400"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  staffList.map((staff) => (
                    <DropdownMenuItem
                      key={staff.id}
                      onSelect={() => setSelectedStaffId(Number(staff.id))}
                      disabled={Number(selectedStaffId) === Number(staff.id)}
                    >
                      <span>{staff.name}</span>
                      {staff.specialization && (
                        <span className="ml-1.5 text-[11px] text-muted-foreground">
                          ({staff.specialization})
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            )}
          </DropdownMenu>

        </div>
      )}
    </header>
  )
}
